/**
 * ContactTriggerFactory.js
 * ------------------------
 * Factory class that dynamically selects and initializes the correct ContactTrigger
 * implementation depending on the runtime environment (Client-side vs. Server-side)
 * with essential error tracking for critical factory operations.
 * 
 * This abstraction allows the contact trigger system to remain SSR-safe while still
 * providing full interactivity on the client. The factory also exposes diagnostic
 * and validation utilities for debugging and integration with hydration workflows.
 * 
 * Responsibilities:
 * - Detect runtime environment using `EnvironmentDetector`
 * - Return the appropriate ContactTrigger constructor or instance (SSR or Client)
 * - Attach shared static helpers to each implementation (e.g., `.hydrate()`, `.getEnvironmentInfo()`)
 * - Provide initialization, debug, and validation utilities for robust integration
 * - Track critical factory operation failures with essential error monitoring
 *
 * Features:
 * - `create()`                     : Selects SSR or Client implementation and enhances it
 * - `hydrate(namespace)`          : Safely instantiates ContactTrigger during hydration phase
 * - `createInstance(namespace)`   : Instantiates the correct implementation
 * - `getEnvironmentInfo()`        : Returns runtime and factory context
 * - `getDebugInfo()`              : Summarizes factory state and available implementations
 * - `initialize()`                : Performs validation and logs factory setup
 * - `validateImplementations()`   : Ensures required implementations and environment detection exist
 * - `forceCreateClient(namespace)` / `forceCreateSSR(namespace)` : Create specific versions for testing
 * - Essential error tracking for factory operation failures
 * - Basic debugging capabilities for troubleshooting production issues
 *
 * Error Handling:
 * - Uses ErrorHandler for essential factory operation failure tracking
 * - Categorizes critical errors: 'environment-detection', 'implementation-creation', 'validation'
 * - Tracks only the most important failures to avoid code bloat
 * - Provides basic error access methods for debugging
 * - Distinguishes between recoverable issues and critical factory failures
 *
 * Limitations:
 * - Assumes `ContactTriggerClient` and `ContactTriggerSSR` are present and valid
 * - Depends on reliable output from `EnvironmentDetector`
 * - Hydration support is framework-agnostic but not reactive (e.g., no auto-listeners)
 * - SSR implementation is assumed to be a no-op — real interactivity only happens on the client
 * - Error tracking is minimal to avoid code explosion (only critical failures)
 * - No detailed factory analytics or performance monitoring
 */

import EnvironmentDetector from '../utils/EnvironmentDetector.js';
import ContactTriggerSSR from './ContactTriggerSSR.js';
import ContactTriggerClient from './ContactTriggerClient.js';
import DebugHelper from '../utils/DebugHelper.js';
import ErrorHandler from '../../utils/ErrorHandler.js';

export default class ContactTriggerFactory {
    /**
     * Initialize error handler for factory operations
     */
    static _getErrorHandler() {
        if (!this._errorHandler) {
            this._errorHandler = new ErrorHandler({
                module: 'ContactTriggerFactory',
                maxErrors: 15
            });
        }
        return this._errorHandler;
    }

    /**
     * Create the appropriate ContactTrigger implementation
     * @returns {Function} ContactTrigger constructor (SSR or Client)
     */
    static create() {
        const errorHandler = this._getErrorHandler();
        
        try {
            const isSSR = EnvironmentDetector.isServerSide();
            const ContactTrigger = isSSR ? ContactTriggerSSR : ContactTriggerClient;
            
            // Validate that the selected implementation exists
            if (!ContactTrigger) {
                throw new Error(`ContactTrigger implementation not available: ${isSSR ? 'SSR' : 'Client'}`);
            }
            
            // Add static methods to the chosen implementation
            this.enhanceImplementation(ContactTrigger);
            
            DebugHelper.info(`ContactTrigger factory created ${isSSR ? 'SSR' : 'Client'} implementation`);
            
            // Track successful creation
            errorHandler.logSuccess('ContactTrigger implementation created successfully', {
                category: 'implementation-creation',
                action: 'success',
                type: isSSR ? 'SSR' : 'Client'
            });
            
            return ContactTrigger;
        } catch (error) {
            errorHandler.handleFatalError('Failed to create ContactTrigger implementation', error, {
                category: 'implementation-creation',
                action: 'failed',
                environmentDetected: EnvironmentDetector.isServerSide() ? 'SSR' : 'Client'
            });
            
            DebugHelper.error('Failed to create ContactTrigger implementation:', error);
            throw error;
        }
    }

    /**
     * Add common static methods to the implementation
     * @param {Function} ContactTriggerImpl The implementation to enhance
     */
    static enhanceImplementation(ContactTriggerImpl) {
        const errorHandler = this._getErrorHandler();
        
        try {
            // Add hydration method
            ContactTriggerImpl.hydrate = this.hydrate.bind(this);
            
            // Add environment detection methods
            ContactTriggerImpl.isServerSide = EnvironmentDetector.isServerSide.bind(EnvironmentDetector);
            ContactTriggerImpl.isClientSide = EnvironmentDetector.isClientSide.bind(EnvironmentDetector);
            
            // Add factory method for creating new instances
            ContactTriggerImpl.createInstance = this.createInstance.bind(this);
            
            // Add environment info getter
            ContactTriggerImpl.getEnvironmentInfo = this.getEnvironmentInfo.bind(this);
            
            // Track successful enhancement
            errorHandler.logSuccess('ContactTrigger implementation enhanced successfully', {
                category: 'implementation-enhancement',
                action: 'success'
            });
            
        } catch (error) {
            errorHandler.handleError('Failed to enhance ContactTrigger implementation', error, {
                category: 'implementation-enhancement',
                action: 'failed'
            });
            
            DebugHelper.error('Failed to enhance ContactTrigger implementation:', error);
        }
    }

    /**
     * Hydration helper for SSR frameworks (Next.js, Nuxt.js, etc.)
     * @param {Object} namespace The contact namespace
     * @returns {Object} ContactTrigger instance
     */
    static hydrate(namespace) {
        const logger = DebugHelper.createContext('ContactTriggerFactory');
        const errorHandler = this._getErrorHandler();
        
        try {
            if (EnvironmentDetector.isClientSide()) {
                logger.info('Hydrating ContactTrigger on client-side...');
                
                if (!ContactTriggerClient) {
                    throw new Error('ContactTriggerClient not available for hydration');
                }
                
                const instance = new ContactTriggerClient(namespace);
                
                // Track successful hydration
                errorHandler.logSuccess('ContactTrigger hydrated successfully', {
                    category: 'hydration',
                    action: 'success',
                    type: 'Client'
                });
                
                return instance;
            } else {
                logger.info('ContactTrigger.hydrate called on server (no-op)');
                
                if (!ContactTriggerSSR) {
                    throw new Error('ContactTriggerSSR not available for server hydration');
                }
                
                return new ContactTriggerSSR(namespace);
            }
        } catch (error) {
            errorHandler.handleError('ContactTrigger hydration failed', error, {
                category: 'hydration',
                action: 'failed',
                environment: EnvironmentDetector.isClientSide() ? 'Client' : 'SSR',
                hasNamespace: !!namespace
            });
            
            logger.error('ContactTrigger hydration failed:', error);
            throw error;
        }
    }

    /**
     * Create a new instance with the appropriate implementation
     * @param {Object} namespace The contact namespace
     * @returns {Object} ContactTrigger instance
     */
    static createInstance(namespace) {
        const logger = DebugHelper.createContext('ContactTriggerFactory');
        const errorHandler = this._getErrorHandler();
        
        try {
            if (EnvironmentDetector.isServerSide()) {
                logger.info('Creating SSR instance');
                
                if (!ContactTriggerSSR) {
                    throw new Error('ContactTriggerSSR implementation not available');
                }
                
                const instance = new ContactTriggerSSR(namespace);
                
                // Track SSR instance creation
                errorHandler.logSuccess('ContactTrigger SSR instance created', {
                    category: 'instance-creation',
                    action: 'success',
                    type: 'SSR'
                });
                
                return instance;
            } else {
                logger.info('Creating Client instance');
                
                if (!ContactTriggerClient) {
                    throw new Error('ContactTriggerClient implementation not available');
                }
                
                const instance = new ContactTriggerClient(namespace);
                
                // Track Client instance creation
                errorHandler.logSuccess('ContactTrigger Client instance created', {
                    category: 'instance-creation',
                    action: 'success',
                    type: 'Client'
                });
                
                return instance;
            }
        } catch (error) {
            errorHandler.handleError('ContactTrigger instance creation failed', error, {
                category: 'instance-creation',
                action: 'failed',
                environment: EnvironmentDetector.isServerSide() ? 'SSR' : 'Client',
                hasNamespace: !!namespace
            });
            
            logger.error('ContactTrigger instance creation failed:', error);
            throw error;
        }
    }

    /**
     * Get environment and factory information
     * @returns {Object} Environment info
     */
    static getEnvironmentInfo() {
        try {
            return {
                environment: EnvironmentDetector.getEnvironmentName(),
                isServerSide: EnvironmentDetector.isServerSide(),
                isClientSide: EnvironmentDetector.isClientSide(),
                factoryVersion: '1.0.0',
                timestamp: new Date().toISOString()
            };
        } catch (error) {
            const errorHandler = this._getErrorHandler();
            errorHandler.handleError('Failed to get environment info', error, {
                category: 'environment-detection',
                action: 'info-failed'
            });
            
            // Return safe defaults
            return {
                environment: 'unknown',
                isServerSide: true, // Safe default
                isClientSide: false,
                factoryVersion: '1.0.0',
                timestamp: new Date().toISOString(),
                error: error.message
            };
        }
    }

    /**
     * Force create client implementation (for testing)
     * @param {Object} namespace The contact namespace
     * @returns {ContactTriggerClient} Client implementation instance
     */
    static forceCreateClient(namespace) {
        const logger = DebugHelper.createContext('ContactTriggerFactory');
        const errorHandler = this._getErrorHandler();
        
        try {
            logger.warn('Force creating client implementation (testing mode)');
            
            if (!ContactTriggerClient) {
                throw new Error('ContactTriggerClient implementation not available for force creation');
            }
            
            const instance = new ContactTriggerClient(namespace);
            
            // Track forced client creation
            errorHandler.logSuccess('ContactTrigger Client force created', {
                category: 'force-creation',
                action: 'success',
                type: 'Client'
            });
            
            return instance;
        } catch (error) {
            errorHandler.handleError('Force create client failed', error, {
                category: 'force-creation',
                action: 'failed',
                type: 'Client'
            });
            
            logger.error('Force create client failed:', error);
            throw error;
        }
    }

    /**
     * Force create SSR implementation (for testing)
     * @param {Object} namespace The contact namespace
     * @returns {ContactTriggerSSR} SSR implementation instance
     */
    static forceCreateSSR(namespace) {
        const logger = DebugHelper.createContext('ContactTriggerFactory');
        const errorHandler = this._getErrorHandler();
        
        try {
            logger.warn('Force creating SSR implementation (testing mode)');
            
            if (!ContactTriggerSSR) {
                throw new Error('ContactTriggerSSR implementation not available for force creation');
            }
            
            const instance = new ContactTriggerSSR(namespace);
            
            // Track forced SSR creation
            errorHandler.logSuccess('ContactTrigger SSR force created', {
                category: 'force-creation',
                action: 'success',
                type: 'SSR'
            });
            
            return instance;
        } catch (error) {
            errorHandler.handleError('Force create SSR failed', error, {
                category: 'force-creation',
                action: 'failed',
                type: 'SSR'
            });
            
            logger.error('Force create SSR failed:', error);
            throw error;
        }
    }

    /**
     * Get debug information about the factory
     * @returns {Object} Factory debug info
     */
    static getDebugInfo() {
        const errorHandler = this._getErrorHandler();
        const errorStats = errorHandler.getErrorStats();
        
        return {
            factoryActive: true,
            environment: this.getEnvironmentInfo(),
            availableImplementations: {
                ssr: !!ContactTriggerSSR,
                client: !!ContactTriggerClient
            },
            currentImplementation: EnvironmentDetector.isServerSide() ? 'SSR' : 'Client',
            errors: {
                total: errorStats.totalErrors,
                fatal: errorStats.fatalErrors,
                recent: errorStats.recentErrors,
                byCategory: errorStats.errorsByCategory
            }
        };
    }

    /**
     * Validate that all required implementations are available
     * @returns {Object} Validation results
     */
    static validateImplementations() {
        const errorHandler = this._getErrorHandler();
        
        const validation = {
            valid: true,
            issues: [],
            implementations: {
                ssr: !!ContactTriggerSSR,
                client: !!ContactTriggerClient
            }
        };

        try {
            if (!ContactTriggerSSR) {
                validation.valid = false;
                validation.issues.push('ContactTriggerSSR implementation missing');
            }

            if (!ContactTriggerClient) {
                validation.valid = false;
                validation.issues.push('ContactTriggerClient implementation missing');
            }

            // Validate environment detector
            try {
                const isSSR = EnvironmentDetector.isServerSide();
                const isClient = EnvironmentDetector.isClientSide();
                
                if (isSSR === isClient) {
                    validation.valid = false;
                    validation.issues.push('Environment detection inconsistent');
                }
            } catch (error) {
                validation.valid = false;
                validation.issues.push(`Environment detection failed: ${error.message}`);
            }

            // Track validation results
            if (validation.valid) {
                errorHandler.logSuccess('Factory validation successful', {
                    category: 'validation',
                    action: 'success'
                });
            } else {
                errorHandler.handleError('Factory validation failed', new Error(validation.issues.join(', ')), {
                    category: 'validation',
                    action: 'failed',
                    issues: validation.issues
                });
            }

        } catch (error) {
            errorHandler.handleError('Validation process failed', error, {
                category: 'validation',
                action: 'process-failed'
            });
            
            validation.valid = false;
            validation.issues.push(`Validation process error: ${error.message}`);
        }

        return validation;
    }

    /**
     * Initialize factory and validate setup
     * @returns {Object} Initialization results
     */
    static initialize() {
        const logger = DebugHelper.createContext('ContactTriggerFactory');
        const errorHandler = this._getErrorHandler();
        
        try {
            logger.info('Initializing ContactTrigger factory...');

            const validation = this.validateImplementations();
            
            if (!validation.valid) {
                logger.error(`Factory validation failed: ${validation.issues.join(', ')}`);
                
                errorHandler.handleFatalError('Factory initialization failed due to validation errors', new Error(validation.issues.join(', ')), {
                    category: 'initialization',
                    action: 'failed',
                    issues: validation.issues
                });
                
                return {
                    success: false,
                    issues: validation.issues,
                    validation
                };
            }

            logger.success('ContactTrigger factory initialized successfully');
            
            // Track successful initialization
            errorHandler.logSuccess('ContactTrigger factory initialized successfully', {
                category: 'initialization',
                action: 'success'
            });
            
            return {
                success: true,
                environment: this.getEnvironmentInfo(),
                validation,
                debug: this.getDebugInfo()
            };
        } catch (error) {
            errorHandler.handleFatalError('Factory initialization process failed', error, {
                category: 'initialization',
                action: 'process-failed'
            });
            
            logger.error('Factory initialization failed:', error);
            
            return {
                success: false,
                error: error.message,
                validation: { valid: false, issues: [`Initialization error: ${error.message}`] }
            };
        }
    }

    /**
     * Get recent errors for debugging
     * @param {number} minutes Number of minutes to look back
     * @returns {Array} Recent error objects
     */
    static getRecentErrors(minutes = 5) {
        const errorHandler = this._getErrorHandler();
        return errorHandler.getRecentErrors(minutes);
    }

    /**
     * Check if there are critical issues
     * @returns {boolean} True if fatal errors exist
     */
    static hasCriticalIssues() {
        const errorHandler = this._getErrorHandler();
        return errorHandler.hasFatalErrors();
    }

    /**
     * Clear error history
     */
    static clearErrorHistory() {
        const errorHandler = this._getErrorHandler();
        errorHandler.clearErrors();
    }
}